import { Button } from "@/components/ui/button";
import { Heading } from "@/components/ui/heading";
import { Input } from "@/components/ui/input";
import { InputGroup } from "@/components/ui/input-group";
import { Text } from "@/components/ui/text";
import { AppDrawer, AppTooltip, Loading } from "@components";
import LayoutHeader from "@components/LayoutHeader";
import { useSearchParamSync } from "@components/PageSearch";
import Panel from "@components/Panel";
import { userId } from "@state/auth";
import { useGoWatch } from "@state/goWatch/state";
import { LayoutGrid, List, Plus, Search } from "lucide-solid";
import { For, Show, createMemo, createSignal, onCleanup } from "solid-js";
import { useService } from "solid-services";
import { css } from "styled-system/css";
import { Box, Flex, Grid } from "styled-system/jsx";
import { scrollbar } from "styled-system/recipes";
import { TOPICS, TOPIC_KEYS, pill } from "@features/communityContent/topics";
import { segButton, segTrack } from "../../Setups/List/Header/segStyles";
import Card from "./Card";
import Create from "./Create";
import Row from "./Row";
import { type GoWatchCols, useState } from "./state";

const COLS: GoWatchCols[] = [3, 4, 5];
const MOBILE_QUERY = '(max-width: 639px)';

function useIsMobile() {
  const mql = typeof window !== 'undefined' ? window.matchMedia(MOBILE_QUERY) : null
  const [isMobile, setIsMobile] = createSignal(mql?.matches ?? false)

  if (mql) {
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    onCleanup(() => mql.removeEventListener('change', onChange))
  }

  return isMobile
}

export default function () {
  const { videosQuery, videos } = useService(useGoWatch)();
  const {
    isCreateFormOpen, setIsCreateFormOpen, editVideo,
    view, setView, cols, setCols, topicFilter, setTopicFilter, search, setSearch,
  } = useService(useState)();

  // Arriving from global search ("Open in Go Watch") shows every topic so no match is hidden
  useSearchParamSync(search, (q) => {
    setSearch(q);
    if (q) setTopicFilter('all');
  });

  const isMobile = useIsMobile();
  const effectiveView = () => isMobile() ? 'card' : view();

  const filtered = createMemo(() => {
    const q = search().trim().toLowerCase();
    const scope = topicFilter();
    return videos().filter((v) => {
      if (scope === 'mine') {
        if (v.profile_id !== userId()) return false;
      } else if (scope !== 'all' && v.topic !== scope) {
        return false;
      }
      if (q && !(`${v.title} ${v.author_username ?? ''} ${v.description ?? ''}`).toLowerCase().includes(q)) return false;
      return true;
    });
  });

  return (
    <Grid gridTemplateRows='auto auto 1fr' gap='0'>
      <LayoutHeader
        leftContent={
          <Flex alignItems='baseline' gap='2' flexWrap='wrap'>
            <Heading fontSize='21px' fontWeight='800' letterSpacing='-.02em' color='editorialInk'>
              Go Watch
            </Heading>
            <Text display={{ base: 'inline', smDown: 'none' }} color='editorialInk3' fontSize='12px'>
              Community video library
            </Text>
          </Flex>
        }
        rightContent={
          <Flex gap='2' alignItems='center' flexWrap='nowrap'>
            <InputGroup
              size='sm'
              startElement={<Search size={15} color='var(--colors-editorial-ink3)' />}
              class={css({ w: { base: '190px', mdDown: '150px', smDown: '120px' }, flexShrink: 1 })}
            >
              <Input
                size='sm'
                placeholder='Search videos…'
                value={search()}
                onInput={(e) => setSearch(e.currentTarget.value)}
                borderRadius='9px'
                borderColor='var(--colors-editorial-hair-strong)'
                bg='editorialPanel'
                color='editorialInk'
                class={css({ _placeholder: { color: 'editorialInk3' } })}
              />
            </InputGroup>

            <AppDrawer
              open={isCreateFormOpen()}
              onOpenChange={({ open }) => setIsCreateFormOpen(open)}
              title={editVideo() ? 'Edit Video' : 'Submit a video'}
              size='md'
              trigger={
                <Button
                  size='sm'
                  bg='editorialAccent'
                  color='editorialBadgeFg'
                  fontWeight='700'
                  fontSize='12.5px'
                  borderRadius='9px'
                  px='15px'
                  gap='7px'
                  flexShrink={0}
                  class={css({ _hover: { filter: 'brightness(1.04)', bg: 'editorialAccent' } })}
                >
                  <Plus size={15} />
                  <Text display={{ base: 'inline', smDown: 'none' }}>Submit video</Text>
                </Button>
              }
            >
              <Create onSuccess={() => setIsCreateFormOpen(false)} />
            </AppDrawer>
          </Flex>
        }
      />

      <Flex
        alignItems='center'
        gap='7px'
        flexWrap='wrap'
        borderBottomWidth='1px'
        borderBottomStyle='solid'
        borderBottomColor='var(--colors-editorial-hair)'
        px={{ base: '22px', smDown: '12px' }}
        py='10px'
      >
        <Flex role='group' aria-label='Filter by topic' gap='7px' alignItems='center' flexWrap='wrap'>
          <button type='button' class={pill(topicFilter() === 'all')} onClick={() => setTopicFilter('all')}>
            All
          </button>
          <For each={TOPIC_KEYS}>
            {(key) => (
              <button type='button' class={pill(topicFilter() === key)} onClick={() => setTopicFilter(key)}>
                <Box as='span' display='inline-block' w='7px' h='7px' borderRadius='full' mr='6px' style={{ background: TOPICS[key].color }} />
                {TOPICS[key].label}
              </button>
            )}
          </For>
          <button type='button' class={pill(topicFilter() === 'mine')} onClick={() => setTopicFilter('mine')}>
            Mine
          </button>
        </Flex>

        <Box flex='1' />

        <Box display={{ base: 'contents', smDown: 'none' }}>
          <div role='group' aria-label='View' class={segTrack}>
            <AppTooltip delay={900} content='Card view'>
              <button type='button' aria-pressed={view() === 'card'} class={segButton(view() === 'card')} onClick={() => setView('card')}>
                <LayoutGrid size={16} />
              </button>
            </AppTooltip>
            <AppTooltip delay={900} content='List view'>
              <button type='button' aria-pressed={view() === 'rail'} class={segButton(view() === 'rail')} onClick={() => setView('rail')}>
                <List size={16} />
              </button>
            </AppTooltip>
          </div>

          <div role='group' aria-label='Cards across' aria-disabled={view() !== 'card'} class={segTrack}>
            <For each={COLS}>
              {(n) => (
                <button
                  type='button'
                  disabled={view() !== 'card'}
                  aria-pressed={cols() === n}
                  class={segButton(cols() === n)}
                  onClick={() => setCols(n)}
                >
                  {n}
                </button>
              )}
            </For>
          </div>
        </Box>
      </Flex>

      <Panel
        display='grid'
        gap='16px'
        height={{
          base: 'calc(var(--vh) * 100 - 130px)',
          mdDown: 'calc(var(--vh) * 100 - 160px)'
        }}
        overflowY='auto'
        class={scrollbar()}
        alignContent='start'
        gridAutoRows='max-content'
      >
        <Show when={videosQuery.isPending}>
          <Loading />
        </Show>

        <Show when={videosQuery.isError}>
          <Text color="error.10">Failed to load videos.</Text>
        </Show>

        <Text fontSize='12px' color='editorialInk3'>
          {filtered().length} video{filtered().length === 1 ? '' : 's'}
          {(() => {
            const t = topicFilter();
            if (t === 'all') return '';
            if (t === 'mine') return ' by you';
            return ` in ${TOPICS[t].label}`;
          })()}
        </Text>

        <Show
          when={effectiveView() === 'card'}
          fallback={
            <Grid gap='12px'>
              <For each={filtered()}>
                {(video) => <Row video={video} />}
              </For>
            </Grid>
          }
        >
          <Grid
            style={{ '--watch-cols': String(cols()) }}
            gap='16px'
            gridTemplateColumns={{
              base: 'repeat(var(--watch-cols), 1fr)',
              lgDown: 'repeat(2, 1fr)',
              smDown: '1fr',
            }}
          >
            <For each={filtered()}>
              {(video) => <Card video={video} />}
            </For>
          </Grid>
        </Show>

        <Show when={!videosQuery.isPending && filtered().length === 0}>
          <Text color="fg.muted" textAlign="center" py="8">
            No videos here yet. Try a different topic, or be the first to submit one.
          </Text>
        </Show>
      </Panel>
    </Grid>
  )
}
